<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import {
  AdminEmptyState,
  AdminFilterBar,
  AdminPageHeader,
  AdminStatusTag,
  RrButton,
  RrInput,
  RrPagination,
  RrSelect,
  RrTag,
  sortParams,
  useListSort
} from '@xjlv2/admin-layout'
import {
  RrDropdown,
  RrMenu,
  RrMenuItem,
  RrModal,
  RrSpin,
  RrTable,
  RrTabs,
  type TableColumnType,
  type TableSorter
} from '@xjlv2/ui'
import { FileText, Image as ImageIcon, LayoutGrid, List, Music, Video } from '@lucide/vue'
import type {
  MaterialStatus,
  MaterialType,
  ModuleMenuItem,
  SaasMaterial,
  SaveSaasMaterialRequest
} from '../../types'
import MaterialAiStudio from './MaterialAiStudio.vue'
import MaterialAiProvidersModal from './MaterialAiProvidersModal.vue'
import MaterialEditorDrawer from './MaterialEditorDrawer.vue'
import MaterialPreviewModal from './MaterialPreviewModal.vue'
import MaterialReferencesModal from './MaterialReferencesModal.vue'
import MaterialUploadModal from './MaterialUploadModal.vue'
import {
  createRacingQueryController,
  formatDateTime,
  loadArticleDetailForAction,
  validateSaveMaterialForm
} from './materialCenterUtils'

const props = withDefaults(defineProps<{
  createApiClient: () => ApiClient
  menu: ModuleMenuItem
  moduleOptions?: Array<{ code: string; name: string }>
  initialModuleCode?: string
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  canAiRead: boolean
  canAiEdit: boolean
  canManageAiProviders: boolean
}>(), {
  moduleOptions: () => [],
  initialModuleCode: ''
})

const emit = defineEmits<{
  notice: [message: string]
  error: [message: string]
  'scope-change': [moduleCode: string]
}>()

type ViewMode = 'grid' | 'list'

const materials = ref<SaasMaterial[]>([])
const loading = ref(false)
const pageNo = ref(1)
const pageSize = ref(20)
const total = ref(0)
const viewMode = ref<ViewMode>('grid')

// 弹窗与抽屉状态
const showEditor = ref(false)
const editingId = ref<number | null>(null)
const previewMaterial = ref<SaasMaterial | null>(null)
const showPreviewModal = ref(false)
const showUploadModal = ref(false)
const showReferencesModal = ref(false)
const referencesMaterial = ref<SaasMaterial | null>(null)
let referencesTrigger: HTMLElement | null = null

const showAiStudio = ref(false)
const showAiProvidersModal = ref(false)
const aiMaterial = ref<SaasMaterial | null>(null)

const racingQuery = createRacingQueryController()

const scopeOptions = computed(() => {
  const rows = props.moduleOptions.filter((option) => option.code !== 'material-center')
  return rows.filter((option, index) => rows.findIndex((item) => item.code === option.code) === index)
})

const initialScope = props.initialModuleCode
  && scopeOptions.value.some((option) => option.code === props.initialModuleCode)
  ? props.initialModuleCode
  : 'ALL'

const selectedModuleCode = ref(initialScope)

const moduleScopeOptions = computed(() => [
  { label: '全部业务', value: 'ALL' },
  ...scopeOptions.value.map((option) => ({ label: option.name, value: option.code }))
])

const statusOptions = [
  { label: '全部状态', value: 'ALL' },
  { label: '草稿', value: 'DRAFT' },
  { label: '启用', value: 'ACTIVE' },
  { label: '已归档', value: 'ARCHIVED' }
]

const isFilterExpanded = ref(false)

function handleSearch() {
  pageNo.value = 1
  void refresh()
}

const filters = ref({
  productCode: '',
  appCode: '',
  type: 'ALL' as 'ALL' | MaterialType,
  status: 'ALL' as 'ALL' | MaterialStatus,
  keyword: '',
  category: '',
  tag: ''
})

const sorting = useListSort({ field: 'updatedAt', direction: 'desc' })

const emptyForm = (): SaveSaasMaterialRequest => ({
  moduleCode: selectedModuleCode.value === 'ALL'
    ? scopeOptions.value[0]?.code ?? ''
    : selectedModuleCode.value,
  productCode: filters.value.productCode,
  appCode: filters.value.appCode,
  type: 'IMAGE',
  status: 'DRAFT',
  name: '',
  title: '',
  fileName: '',
  fileUrl: '',
  objectKey: '',
  coverUrl: '',
  mimeType: '',
  tags: [],
  content: '',
  description: '',
  copyright: '',
  source: 'saas'
})

const form = ref<SaveSaasMaterialRequest>(emptyForm())
const tagText = ref('')

const typeOptions: Array<{ value: MaterialType; label: string }> = [
  { value: 'IMAGE', label: '图片' },
  { value: 'ARTICLE', label: '文稿' },
  { value: 'AUDIO', label: '音频' },
  { value: 'VIDEO', label: '视频' }
]

function handleTypeFilter(type: 'ALL' | MaterialType) {
  if (filters.value.type === type) return
  filters.value.type = type
  pageNo.value = 1
  void refresh()
}

const typeTabItems = computed(() => [
  { key: 'ALL', label: '全部素材' },
  ...typeOptions.map((item) => ({ key: item.value, label: item.label }))
])

const tableColumns = computed<TableColumnType<SaasMaterial>[]>(() => [
  {
    key: 'thumb',
    title: '缩略图',
    width: 72,
    align: 'center'
  },
  {
    key: 'name',
    dataIndex: 'name',
    title: '素材',
    sorter: true,
    sortOrder: sorting.sortState.value.field === 'name'
      ? (sorting.sortState.value.direction === 'asc' ? 'ascend' : 'descend')
      : null,
    ellipsis: true
  },
  {
    key: 'moduleCode',
    dataIndex: 'moduleCode',
    title: '业务归属',
    width: 200
  },
  {
    key: 'type',
    dataIndex: 'type',
    title: '类型 / 大小',
    sorter: true,
    sortOrder: sorting.sortState.value.field === 'type'
      ? (sorting.sortState.value.direction === 'asc' ? 'ascend' : 'descend')
      : null,
    width: 140
  },
  {
    key: 'status',
    dataIndex: 'status',
    title: '状态',
    sorter: true,
    sortOrder: sorting.sortState.value.field === 'status'
      ? (sorting.sortState.value.direction === 'asc' ? 'ascend' : 'descend')
      : null,
    width: 100
  },
  {
    key: 'updatedAt',
    dataIndex: 'updatedAt',
    title: '更新时间',
    sorter: true,
    sortOrder: sorting.sortState.value.field === 'updatedAt'
      ? (sorting.sortState.value.direction === 'asc' ? 'ascend' : 'descend')
      : null,
    width: 170
  },
  {
    key: 'actions',
    title: '操作',
    width: 240,
    align: 'right'
  }
])

function handleTableChange(
  _pagination: unknown,
  _filters: unknown,
  sorter: TableSorter<SaasMaterial>
) {
  if (sorter && sorter.field) {
    const field = Array.isArray(sorter.field) ? String(sorter.field[0]) : String(sorter.field)
    const direction = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : undefined
    sorting.sortState.value = {
      field: direction ? field : null,
      direction: direction ?? 'desc'
    }
  }
}

function typeLabel(type: MaterialType) {
  return typeOptions.find((item) => item.value === type)?.label ?? type
}

function typeIcon(type: MaterialType) {
  if (type === 'AUDIO') return Music
  if (type === 'VIDEO') return Video
  if (type === 'ARTICLE') return FileText
  return ImageIcon
}

function moduleLabel(moduleCode: string) {
  return scopeOptions.value.find((option) => option.code === moduleCode)?.name ?? moduleCode
}

function statusLabel(status: MaterialStatus) {
  return { DRAFT: '草稿', ACTIVE: '启用', ARCHIVED: '已归档' }[status]
}

function statusToTag(status: MaterialStatus): 'success' | 'warning' | 'default' {
  if (status === 'ACTIVE') return 'success'
  if (status === 'DRAFT') return 'warning'
  return 'default'
}

function materialTags(material: SaasMaterial) {
  if (Array.isArray(material.tags)) return material.tags
  return (material.tags ?? '').split(',').map((tag) => tag.trim()).filter(Boolean)
}

function fileSize(value: number | null | undefined) {
  if (value == null) return '-'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / 1024 / 1024).toFixed(1)} MB`
}

function formatMaterialSubInfo(material: SaasMaterial) {
  const module = moduleLabel(material.moduleCode)
  if (material.type === 'ARTICLE') {
    const charCount = material.content ? `${material.content.length} 字` : '正文素材'
    return `${module} · ${material.fileName || material.title || '文稿'} · ${charCount}`
  }
  return `${module} · ${material.fileName || '正文素材'} · ${fileSize(material.fileSize)}`
}

function materialSizeOrLength(material: SaasMaterial) {
  if (material.type === 'ARTICLE') {
    return material.content ? `${material.content.length} 字` : '文稿素材'
  }
  return fileSize(material.fileSize)
}

function buildQuery(moduleCode?: string) {
  const query = new URLSearchParams()
  if (moduleCode) query.set('moduleCode', moduleCode)
  for (const [key, value] of Object.entries(filters.value)) {
    if (value && value !== 'ALL') {
      const trimmed = typeof value === 'string' ? value.trim() : value
      if (trimmed) query.set(key, trimmed)
    }
  }
  query.set('pageNo', String(pageNo.value))
  query.set('pageSize', String(pageSize.value))
  const sort = sortParams(sorting.sortState.value)
  if (sort.sortField) query.set('sortField', sort.sortField)
  if (sort.sortOrder) query.set('sortOrder', sort.sortOrder)
  return query.toString()
}

async function refresh() {
  const queryId = racingQuery.nextQueryId()
  loading.value = true
  emit('error', '')
  try {
    const api = props.createApiClient()
    const moduleCode = selectedModuleCode.value === 'ALL' ? undefined : selectedModuleCode.value
    const result = await api.get<{ records: SaasMaterial[]; total: number; pageNo: number; pageSize: number }>(
      `/api/saas/system/materials?${buildQuery(moduleCode)}`
    )
    if (!racingQuery.isLatest(queryId)) return
    materials.value = result.records
    total.value = result.total
    pageNo.value = result.pageNo
    pageSize.value = result.pageSize
  } catch (err) {
    if (!racingQuery.isLatest(queryId)) return
    emit('error', err instanceof Error ? err.message : '素材加载失败')
  } finally {
    if (racingQuery.isLatest(queryId)) {
      loading.value = false
    }
  }
}

function handlePageChange(current: number, size: number) {
  if (size !== pageSize.value) {
    pageSize.value = size
    pageNo.value = 1
  } else {
    pageNo.value = current
  }
  void refresh()
}

function resetFilters() {
  filters.value = {
    productCode: '',
    appCode: '',
    type: 'ALL',
    status: 'ALL',
    keyword: '',
    category: '',
    tag: ''
  }
  pageNo.value = 1
  void refresh()
}

function openUpload() {
  if (!scopeOptions.value.length) {
    emit('error', '当前租户没有可作为素材归属的业务模块。')
    return
  }
  showUploadModal.value = true
}

function openCreate() {
  if (!scopeOptions.value.length) {
    emit('error', '当前租户没有可作为素材归属的业务模块。')
    return
  }
  editingId.value = null
  form.value = emptyForm()
  tagText.value = ''
  showEditor.value = true
}

async function openPreview(material: SaasMaterial) {
  try {
    const target = await loadArticleDetailForAction(material, (id) =>
      props.createApiClient().get<SaasMaterial>(`/api/saas/system/materials/${id}`)
    )
    previewMaterial.value = target
    showPreviewModal.value = true
  } catch (err) {
    previewMaterial.value = null
    showPreviewModal.value = false
    emit('error', err instanceof Error ? err.message : '获取文稿预览失败')
  }
}

async function openEdit(material: SaasMaterial) {
  let target: SaasMaterial
  try {
    target = await loadArticleDetailForAction(material, (id) =>
      props.createApiClient().get<SaasMaterial>(`/api/saas/system/materials/${id}`)
    )
  } catch (err) {
    emit('error', err instanceof Error ? `${err.message}，无法编辑` : '获取文稿详情失败，无法编辑')
    return
  }
  editingId.value = target.id
  form.value = {
    moduleCode: target.moduleCode,
    productCode: target.productCode ?? '',
    appCode: target.appCode ?? '',
    type: target.type,
    status: target.status,
    name: target.name,
    title: target.title ?? '',
    fileName: target.fileName ?? '',
    fileUrl: target.fileUrl ?? '',
    objectKey: target.objectKey ?? '',
    coverUrl: target.coverUrl ?? '',
    mimeType: target.mimeType ?? '',
    fileSize: target.fileSize ?? undefined,
    durationSeconds: target.durationSeconds ?? undefined,
    width: target.width ?? undefined,
    height: target.height ?? undefined,
    category: target.category ?? '',
    tags: materialTags(target),
    content: target.content ?? '',
    description: target.description ?? '',
    copyright: target.copyright ?? '',
    source: target.source ?? 'saas'
  }
  tagText.value = materialTags(target).join(', ')
  showEditor.value = true
}

function openAiEditor(material: SaasMaterial) {
  aiMaterial.value = material
  showAiStudio.value = true
}

function openAiProviders() {
  showAiProvidersModal.value = true
}

async function handleAiApplied() {
  showAiStudio.value = false
  await refresh()
}

async function saveMaterial() {
  const validation = validateSaveMaterialForm(form.value)
  if (!validation.valid) {
    emit('error', validation.error || '素材信息校验失败')
    return
  }
  loading.value = true
  try {
    const payload: SaveSaasMaterialRequest = {
      ...form.value,
      name: form.value.name.trim(),
      title: form.value.title?.trim() || undefined,
      category: form.value.category?.trim() || undefined,
      tags: tagText.value.split(',').map((tag) => tag.trim()).filter(Boolean)
    }
    const api = props.createApiClient()
    if (editingId.value == null) {
      await api.post<SaasMaterial>('/api/saas/system/materials', payload)
    } else {
      await api.put<SaasMaterial>(`/api/saas/system/materials/${editingId.value}`, payload)
    }
    showEditor.value = false
    emit('notice', editingId.value == null ? '素材已登记。' : '素材信息已保存。')
    await refresh()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '素材保存失败')
  } finally {
    loading.value = false
  }
}

function openReferences(material: SaasMaterial) {
  referencesMaterial.value = material
  showReferencesModal.value = true
}

function handleOpenReferencesFromPreview(material: SaasMaterial, event: MouseEvent) {
  rememberReferencesTrigger(event)
  showPreviewModal.value = false
  openReferences(material)
}

function rememberReferencesTrigger(event: MouseEvent) {
  referencesTrigger = event.currentTarget instanceof HTMLElement ? event.currentTarget : null
}

function restoreReferencesFocus() {
  if (referencesTrigger?.isConnected) referencesTrigger.focus({ preventScroll: true })
  referencesTrigger = null
}

interface ActionConfirmState {
  open: boolean
  title: string
  message: string
  danger?: boolean
  confirmText?: string
  action: () => Promise<void>
}

const actionConfirm = ref<ActionConfirmState>({
  open: false,
  title: '',
  message: '',
  danger: false,
  confirmText: '确定',
  action: async () => {}
})

function triggerArchive(material: SaasMaterial) {
  if ((material.referenceCount ?? 0) > 0) {
    actionConfirm.value = {
      open: true,
      title: '归档素材确认',
      message: `素材“${material.name}”当前被引用 ${material.referenceCount} 次。归档后业务消费方将无法正常交付该素材，确定归档吗？`,
      danger: true,
      confirmText: '确认归档',
      action: () => executeUpdateStatus(material, 'ARCHIVED')
    }
    return
  }
  executeUpdateStatus(material, 'ARCHIVED')
}

async function updateStatus(material: SaasMaterial, status: MaterialStatus) {
  if (status === 'ARCHIVED') {
    triggerArchive(material)
    return
  }
  await executeUpdateStatus(material, status)
}

async function executeUpdateStatus(material: SaasMaterial, status: MaterialStatus) {
  actionConfirm.value.open = false
  loading.value = true
  try {
    await props.createApiClient().put<SaasMaterial>(`/api/saas/system/materials/${material.id}/status`, { status })
    emit('notice', status === 'ACTIVE' ? '素材已启用。' : '素材已归档。')
    await refresh()
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '素材状态更新失败')
  } finally {
    loading.value = false
  }
}

function deleteMaterial(material: SaasMaterial) {
  actionConfirm.value = {
    open: true,
    title: '永久删除素材确认',
    message: `确定永久删除素材“${material.name}”吗？此操作不可恢复。`,
    danger: true,
    confirmText: '确认删除',
    action: () => executeDeleteMaterial(material)
  }
}

async function executeDeleteMaterial(material: SaasMaterial) {
  actionConfirm.value.open = false
  loading.value = true
  try {
    await props.createApiClient().delete<void>(`/api/saas/system/materials/${material.id}`)
    emit('notice', '素材已永久删除。')
    if (materials.value.length === 1 && pageNo.value > 1) {
      pageNo.value--
    }
    await refresh()
  } catch (err) {
    const message = err instanceof Error ? err.message : '素材删除失败'
    emit('error', message.includes('MATERIAL_IN_USE') ? '素材正在被产品内容引用，请解除引用后再删除。' : message)
  } finally {
    loading.value = false
  }
}

async function copyUrl(material: SaasMaterial) {
  try {
    if (!material.fileUrl) throw new Error('empty url')
    await navigator.clipboard.writeText(material.fileUrl)
    emit('notice', '素材 URL 已复制。')
  } catch {
    emit('error', '浏览器未允许复制，请手动复制素材 URL。')
  }
}

watch(() => props.initialModuleCode, (moduleCode) => {
  if (moduleCode && scopeOptions.value.some((option) => option.code === moduleCode)) {
    selectedModuleCode.value = moduleCode
  }
})

watch(selectedModuleCode, (moduleCode) => {
  showEditor.value = false
  showUploadModal.value = false
  previewMaterial.value = null
  showPreviewModal.value = false
  showReferencesModal.value = false
  referencesMaterial.value = null
  showAiStudio.value = false
  showAiProvidersModal.value = false
  aiMaterial.value = null
  pageNo.value = 1
  emit('scope-change', moduleCode === 'ALL' ? '' : moduleCode)
  void refresh()
}, { immediate: true })

watch(() => sorting.sortState.value, () => {
  pageNo.value = 1
  void refresh()
}, { deep: true })

onUnmounted(() => {
  referencesTrigger = null
})

defineExpose({ refresh })
</script>

<template>
  <section class="panel span management-list material-manager">
    <AdminPageHeader
      title="素材中心"
      class="material-page-header"
    />

    <RrTabs
      :active-key="filters.type"
      :items="typeTabItems"
      class="material-type-tabs"
      @update:active-key="(val) => handleTypeFilter(val as any)"
    />

    <AdminFilterBar
      :loading="loading"
      :expandable="true"
      v-model:expanded="isFilterExpanded"
      class="material-filter-bar"
      @submit="handleSearch"
      @reset="resetFilters"
    >
      <RrSelect
        v-model:value="selectedModuleCode"
        :options="moduleScopeOptions"
        placeholder="业务模块"
        aria-label="业务模块"
      />
      <RrSelect
        v-model:value="filters.status"
        :options="statusOptions"
        placeholder="素材状态"
        aria-label="素材状态"
      />
      <RrInput
        v-model:value="filters.keyword"
        placeholder="搜索名称、标题、文件名"
        allow-clear
        @press-enter="handleSearch"
      />

      <template #extra>
        <RrInput v-model:value="filters.productCode" placeholder="产品编码" allow-clear />
        <RrInput v-model:value="filters.appCode" placeholder="应用编码" allow-clear />
        <RrInput v-model:value="filters.category" placeholder="全部分类" allow-clear />
        <RrInput v-model:value="filters.tag" placeholder="标签" allow-clear />
      </template>
    </AdminFilterBar>

    <div class="material-action-bar">
      <div class="action-btn-group">
        <RrButton type="primary" :disabled="loading || !canCreate || !scopeOptions.length" @click="openUpload">上传素材</RrButton>
        <RrButton :disabled="loading || !canCreate || !scopeOptions.length" title="登记外部链接或文稿正文" @click="openCreate">登记外链/文稿</RrButton>
      </div>
      <div class="action-tools-group">
        <RrButton v-if="canManageAiProviders" :disabled="loading" @click="openAiProviders">AI 提供商</RrButton>
        <span class="material-total-count">共 {{ total }} 个素材</span>
        <div class="view-switch" role="group" aria-label="素材展示方式">
          <RrButton
            size="small"
            :type="viewMode === 'grid' ? 'primary' : 'default'"
            title="网格视图"
            aria-label="网格视图"
            @click="viewMode = 'grid'"
          >
            <LayoutGrid :size="15" />
          </RrButton>
          <RrButton
            size="small"
            :type="viewMode === 'list' ? 'primary' : 'default'"
            title="列表视图"
            aria-label="列表视图"
            @click="viewMode = 'list'"
          >
            <List :size="15" />
          </RrButton>
        </div>
      </div>
    </div>

    <div v-if="loading && materials.length === 0" class="material-state-wrap">
      <RrSpin tip="正在加载素材..." />
    </div>

    <div v-else-if="materials.length === 0" class="material-state-wrap">
      <AdminEmptyState
        title="暂无匹配素材"
        description="当前筛选条件下未检索到相关素材，您可以重置筛选条件或立即上传新素材。"
      >
        <template #actions>
          <RrButton type="default" @click="resetFilters">重置筛选</RrButton>
          <RrButton
            v-if="canCreate && scopeOptions.length"
            type="primary"
            @click="openUpload"
          >
            上传素材
          </RrButton>
        </template>
      </AdminEmptyState>
    </div>

    <!-- 网格卡片视图 -->
    <div v-else-if="viewMode === 'grid'" class="material-grid">
      <article v-for="material in materials" :key="material.id" class="material-card">
        <div
          class="material-preview-box"
          role="button"
          tabindex="0"
          :aria-label="`预览 ${material.name}`"
          @click="openPreview(material)"
          @keydown.enter="openPreview(material)"
        >
          <img
            v-if="material.type === 'IMAGE' && material.fileUrl"
            :src="material.coverUrl || material.fileUrl"
            :alt="material.name"
          />
          <img
            v-else-if="material.coverUrl"
            :src="material.coverUrl"
            :alt="`${material.name}封面`"
          />
          <div v-else class="type-icon-wrapper">
            <component :is="typeIcon(material.type)" :size="36" class="type-icon" />
          </div>
          <span class="type-badge">{{ typeLabel(material.type) }}</span>
        </div>

        <div class="material-card-body">
          <div class="card-title-row">
            <strong :title="material.name">{{ material.name }}</strong>
            <AdminStatusTag
              :status="statusToTag(material.status)"
              :text="statusLabel(material.status)"
              :dot="false"
            />
          </div>

          <div class="card-sub-info">
            {{ formatMaterialSubInfo(material) }}
          </div>

          <div class="tag-row">
            <RrTag v-for="tag in materialTags(material).slice(0, 3)" :key="tag">{{ tag }}</RrTag>
          </div>

          <div class="card-actions">
            <RrButton size="small" :disabled="!canUpdate" @click="openEdit(material)">编辑</RrButton>
            <RrButton
              size="small"
              :disabled="!canAiRead && !canAiEdit"
              @click="openAiEditor(material)"
            >
              {{ canAiEdit ? 'AI 编辑' : 'AI 历史' }}
            </RrButton>
            <RrDropdown placement="bottomRight" :trigger="['click']">
              <RrButton
                size="small"
                class="more-btn"
                aria-label="更多操作"
                @click="rememberReferencesTrigger"
              >
                更多 ▾
              </RrButton>
              <template #overlay>
                <RrMenu class="action-dropdown-menu">
                  <RrMenuItem @click="openReferences(material)">
                    引用明细 ({{ material.referenceCount ?? 0 }})
                  </RrMenuItem>
                  <RrMenuItem :disabled="!material.fileUrl" @click="copyUrl(material)">
                    复制 URL
                  </RrMenuItem>
                  <RrMenuItem
                    :disabled="!canUpdate || loading"
                    :divided="true"
                    @click="updateStatus(material, material.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE')"
                  >
                    {{ material.status === 'ACTIVE' ? '归档素材' : '启用素材' }}
                  </RrMenuItem>
                  <RrMenuItem
                    :disabled="!canDelete || loading"
                    danger
                    @click="deleteMaterial(material)"
                  >
                    删除素材
                  </RrMenuItem>
                </RrMenu>
              </template>
            </RrDropdown>
          </div>
        </div>
      </article>
    </div>

    <!-- 列表表格视图 -->
    <div v-else class="material-table-wrap">
      <RrTable
        :columns="tableColumns"
        :data-source="materials"
        row-key="id"
        :loading="loading"
        :pagination="false"
        @change="handleTableChange"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'thumb'">
            <div
              class="table-thumb"
              role="button"
              tabindex="0"
              :aria-label="`预览 ${record.name}`"
              @click="openPreview(record)"
              @keydown.enter="openPreview(record)"
            >
              <img
                v-if="record.type === 'IMAGE' && record.fileUrl"
                :src="record.coverUrl || record.fileUrl"
                :alt="record.name"
              />
              <img
                v-else-if="record.coverUrl"
                :src="record.coverUrl"
                :alt="`${record.name}封面`"
              />
              <component :is="typeIcon(record.type)" v-else :size="20" class="thumb-fallback-icon" />
            </div>
          </template>

          <template v-else-if="column.key === 'name'">
            <div class="cell-main">
              <strong :title="record.name">{{ record.name }}</strong>
              <small :title="record.fileName || record.fileUrl || undefined">{{ record.fileName || record.fileUrl || '正文素材' }}</small>
            </div>
          </template>

          <template v-else-if="column.key === 'moduleCode'">
            <div class="cell-sub">
              <strong>{{ moduleLabel(record.moduleCode) }}</strong>
              <small>{{ record.productCode || '全部产品' }} · {{ record.appCode || '全部应用' }}</small>
            </div>
          </template>

          <template v-else-if="column.key === 'type'">
            <div class="cell-type">
              <strong>{{ typeLabel(record.type) }}</strong>
              <small>{{ materialSizeOrLength(record) }}</small>
            </div>
          </template>

          <template v-else-if="column.key === 'status'">
            <AdminStatusTag
              :status="statusToTag(record.status)"
              :text="statusLabel(record.status)"
              :dot="false"
            />
          </template>

          <template v-else-if="column.key === 'updatedAt'">
            <span class="cell-time">
              {{ formatDateTime(record.updatedAt) }}
            </span>
          </template>

          <template v-else-if="column.key === 'actions'">
            <div class="cell-actions">
              <RrButton size="small" @click="openPreview(record)">预览</RrButton>
              <RrButton size="small" :disabled="!canUpdate" @click="openEdit(record)">编辑</RrButton>
              <RrButton
                size="small"
                :disabled="!canAiRead && !canAiEdit"
                @click="openAiEditor(record)"
              >
                {{ canAiEdit ? 'AI 编辑' : 'AI 历史' }}
              </RrButton>
              <RrDropdown placement="bottomRight" :trigger="['click']">
                <RrButton
                  size="small"
                  class="more-btn"
                  aria-label="更多操作"
                  @click="rememberReferencesTrigger"
                >
                  更多 ▾
                </RrButton>
                <template #overlay>
                  <RrMenu class="action-dropdown-menu">
                    <RrMenuItem @click="openReferences(record)">
                      引用明细 ({{ record.referenceCount ?? 0 }})
                    </RrMenuItem>
                    <RrMenuItem :disabled="!record.fileUrl" @click="copyUrl(record)">
                      复制 URL
                    </RrMenuItem>
                    <RrMenuItem
                      :disabled="!canUpdate || loading"
                      :divided="true"
                      @click="updateStatus(record, record.status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE')"
                    >
                      {{ record.status === 'ACTIVE' ? '归档素材' : '启用素材' }}
                    </RrMenuItem>
                    <RrMenuItem
                      :disabled="!canDelete || loading"
                      danger
                      @click="deleteMaterial(record)"
                    >
                      删除素材
                    </RrMenuItem>
                  </RrMenu>
                </template>
              </RrDropdown>
            </div>
          </template>
        </template>
      </RrTable>
    </div>

    <!-- 分页 -->
    <div v-if="total > 0" class="material-pagination-wrap">
      <RrPagination
        :current="pageNo"
        :page-size="pageSize"
        :total="total"
        :show-total="(t) => `共 ${t} 个素材`"
        :show-size-changer="true"
        :show-quick-jumper="true"
        :page-size-options="['10', '20', '50', '100']"
        :disabled="loading"
        align="between"
        @change="handlePageChange"
      />
    </div>

    <!-- 上传弹窗 -->
    <MaterialUploadModal
      v-model:open="showUploadModal"
      :scope-options="scopeOptions"
      :initial-scope="selectedModuleCode === 'ALL' ? (scopeOptions[0]?.code ?? '') : selectedModuleCode"
      :default-category="filters.category"
      :default-product-code="filters.productCode"
      :default-app-code="filters.appCode"
      :create-api-client="createApiClient"
      @notice="(message) => emit('notice', message)"
      @error="(message) => emit('error', message)"
      @uploaded="refresh"
    />

    <!-- 外链/文稿编辑抽屉 -->
    <MaterialEditorDrawer
      v-model:open="showEditor"
      :editing-id="editingId"
      :form="form"
      v-model:tag-text="tagText"
      :scope-options="scopeOptions"
      :type-options="typeOptions"
      :loading="loading"
      :can-create="canCreate"
      :can-update="canUpdate"
      @save="saveMaterial"
    />

    <!-- 媒体预览弹窗 -->
    <MaterialPreviewModal
      v-model:open="showPreviewModal"
      :material="previewMaterial"
      @open-references="handleOpenReferencesFromPreview"
    />

    <!-- 引用明细弹窗 -->
    <MaterialReferencesModal
      v-model:open="showReferencesModal"
      :material="referencesMaterial"
      :create-api-client="createApiClient"
      @error="(message) => emit('error', message)"
      @after-close="restoreReferencesFocus"
    />

    <!-- 二次确认弹窗 -->
    <RrModal
      :open="actionConfirm.open"
      :title="actionConfirm.title"
      :width="440"
      :ok-text="actionConfirm.confirmText || '确定'"
      cancel-text="取消"
      ok-type="primary"
      :ok-button-props="{ danger: actionConfirm.danger }"
      :confirm-loading="loading"
      @update:open="(val) => { if (!val) actionConfirm.open = false }"
      @ok="actionConfirm.action()"
      @cancel="actionConfirm.open = false"
    >
      <p class="confirm-modal-msg">{{ actionConfirm.message }}</p>
    </RrModal>

    <!-- AI Studio 工作台 -->
    <MaterialAiStudio
      v-if="showAiStudio"
      :key="aiMaterial?.id || 'ai-studio'"
      :create-api-client="createApiClient"
      :module-code="aiMaterial?.moduleCode || (selectedModuleCode === 'ALL' ? scopeOptions[0]?.code || '' : selectedModuleCode)"
      :material="aiMaterial"
      :can-read="canAiRead"
      :can-edit="canAiEdit"
      :can-manage-providers="canManageAiProviders"
      @close="showAiStudio = false"
      @applied="handleAiApplied"
      @notice="(message) => emit('notice', message)"
      @error="(message) => emit('error', message)"
    />

    <!-- AI 提供商配置独立弹窗 -->
    <MaterialAiProvidersModal
      v-model:open="showAiProvidersModal"
      :create-api-client="createApiClient"
      @notice="(message) => emit('notice', message)"
      @error="(message) => emit('error', message)"
    />
  </section>
</template>

<style scoped>
.material-page-header {
  margin-bottom: 16px;
}

.material-total-count {
  font-size: 13px;
  color: var(--rr-color-text-muted);
}

.view-switch {
  display: flex;
  align-items: center;
  border: 1px solid var(--rr-color-border);
  border-radius: 6px;
  padding: 2px;
}

.view-switch button {
  display: grid;
  width: 32px;
  height: 28px;
  place-items: center;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--rr-color-text-muted);
  cursor: pointer;
  transition: background 150ms ease-out, color 150ms ease-out;
}

.view-switch button:hover {
  color: var(--rr-color-text-strong);
}

.view-switch button.active {
  background: var(--rr-color-primary);
  color: var(--rr-color-surface);
}

/* 快速类型筛选 Tab */
.material-type-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 14px;
  overflow-x: auto;
}

.type-tab-btn {
  border: 1px solid var(--rr-color-border);
  border-radius: 6px;
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 150ms ease-out, color 150ms ease-out, border-color 150ms ease-out;
}

.type-tab-btn:hover {
  background: var(--rr-color-surface);
  color: var(--rr-color-text-strong);
}

.type-tab-btn.active {
  background: var(--rr-color-primary);
  color: var(--rr-color-surface);
  border-color: var(--rr-color-primary);
  font-weight: 600;
}

/* 筛选与业务操作栏 */
.material-filter-bar {
  margin-bottom: 12px;
}

.material-action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.action-btn-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-tools-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.material-state-wrap {
  padding: 32px 0;
  text-align: center;
}

.loading-hint {
  font-size: 14px;
  color: var(--rr-color-text-muted);
}

/* 网格视图 */
.material-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.material-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--rr-color-border);
  border-radius: 8px;
  background: var(--rr-color-surface);
  transition: border-color 200ms ease-out, box-shadow 200ms ease-out, transform 200ms ease-out;
}

.material-card:hover {
  border-color: var(--rr-color-border-strong);
  box-shadow: var(--rr-shadow-1);
  transform: translateY(-2px);
}

.material-preview-box {
  position: relative;
  display: grid;
  width: 100%;
  height: 160px;
  place-items: center;
  border: 0;
  border-bottom: 1px solid var(--rr-color-border);
  background: var(--rr-color-canvas);
  padding: 0;
  overflow: hidden;
  cursor: pointer;
}

.material-preview-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 200ms ease-out;
}

.material-card:hover .material-preview-box img {
  transform: scale(1.02);
}

.type-icon-wrapper {
  color: var(--rr-color-text-muted);
}

.type-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  border-radius: 4px;
  background: rgba(23, 26, 31, 0.75);
  color: var(--rr-color-surface);
  padding: 3px 6px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.material-card-body {
  display: grid;
  gap: 8px;
  padding: 12px;
}

.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.card-title-row strong {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--rr-color-text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-sub-info {
  font-size: 12px;
  color: var(--rr-color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 22px;
  flex-wrap: wrap;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 4px;
  border-top: 1px solid var(--rr-color-surface-subtle);
}

/* 列表表格视图 */
.material-table-wrap {
  width: 100%;
}

.table-thumb {
  width: 44px;
  height: 44px;
  border-radius: 4px;
  background: var(--rr-color-canvas);
  border: 1px solid var(--rr-color-border);
  overflow: hidden;
  display: grid;
  place-items: center;
  padding: 0;
  cursor: pointer;
}

.table-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumb-fallback-icon {
  color: var(--rr-color-text-muted);
}

.cell-main,
.cell-sub,
.cell-type {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.cell-main strong,
.cell-sub strong,
.cell-type strong {
  font-size: 13px;
  font-weight: 600;
  color: var(--rr-color-text-strong);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-main small,
.cell-sub small,
.cell-type small {
  color: var(--rr-color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-time {
  color: var(--rr-color-text-muted);
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.cell-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: nowrap;
}

.material-pagination-wrap {
  margin-top: 20px;
  padding: 12px 0 4px;
}

/* 二次确认弹窗 */
.confirm-modal-msg {
  font-size: 14px;
  color: var(--rr-color-text);
  line-height: 1.6;
  margin: 0;
  padding: 8px 0;
}

/* 响应式适配 */
@media (max-width: 900px) {
  .material-action-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
  .material-action-bar .action-btn-group {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  .material-action-bar .action-tools-group {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }
}

@media (max-width: 600px) {
  .material-grid {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  .material-card,
  .material-card:hover {
    transition: none !important;
    transform: none !important;
  }
}
</style>
